<template lang="pug">
el-table(
  v-loading="loading",
  :data="assets",
  :default-sort="defaultSort",
  row-class-name="cursor-pointer",
  table-layout="auto",
  style="width: 100%",
  @row-click="onRowClick",
  @sort-change="sortChange"
)
  el-table-column(
    v-if="showId",
    prop="twin.uuid",
    min-width="300",
    :label="$t('id')"
  )
  el-table-column(
    prop="twin.name",
    min-width="300",
    :label="$t('name')",
    show-overflow-tooltip
  )
    template(#default="scope")
      slot(
        v-if="hasSlot('name')",
        name="name",
        v-bind="{ data: scope.row, value: scope.row.twin.name }"
      )
      span(v-else) {{ scope.row.twin.name }}

  el-table-column(
    v-if="showDescription",
    prop="twin.description",
    min-width="300",
    :label="$t('description')",
    show-overflow-tooltip
  )
  el-table-column(
    v-for="property in assetProperties",
    :key="property.uuid",
    min-width="205",
    :label="property.name",
    :prop="property.name",
    :sortable="['float', 'text'].includes(property.type) ? 'custom' : undefined"
  )
    template(#default="scope")
      slot(
        v-if="hasSlot(property.customId)",
        :name="property.customId",
        v-bind="{ data: scope.row, value: scope.row[property.customId] }"
      )
      component(
        v-else-if="getTableRenderer(property)?.viewComponentTable",
        :is="getTableRenderer(property)?.viewComponentTable",
        :value="scope.row[property.customId]"
      )
      .smx-comma-separated-row(v-else-if="property.type === 'twinRelation'")
        TwinField(
          v-for="twin in scope.row[property.customId]",
          :key="twin.uuid",
          :twin="{ uuid: twin.uuid, name: twin.name, templateId: twin.templateId }",
          @click.stop
        )
      UsersField(
        v-else-if="property.type === 'userRelationArray'",
        :user-ids="scope.row[property.customId]"
      )
      span(v-else-if="property.type === 'float'") {{ isNil(scope.row[property.customId]) ? "-" : scope.row[property.customId] }}
      span(v-else-if="property.constraint.type === 'date'") {{ scope.row[property.customId] ? formatDate(scope.row[property.customId]) : "-" }}
      span(v-else-if="property.constraint.type === 'year'") {{ scope.row[property.customId] ? formatYear(scope.row[property.customId]) : "-" }}
      span(v-else-if="property.constraint.type === 'boolean'") {{ scope.row[property.customId] ? formatYesNo(scope.row[property.customId]) : "-" }}
      span(v-else) {{ scope.row[property.customId] || "-" }}

  el-table-column(
    v-if="showCreated",
    min-width="200",
    :label="$t('createdAt')",
    prop="twin.created",
    :formatter="formatCreatedAt"
  )
  el-table-column(v-if="!hideDelete", width="50")
    template(#default="scope")
      el-popconfirm(
        :title="$t('confirmDelete')",
        :confirm-button-text="$t('delete')",
        :cancel-button-text="$t('cancel')",
        width="220",
        @confirm="deleteTwin(scope.row)"
      )
        template(#reference)
          el-icon(@click.stop="")
            Delete

.flex.justify-center.mt-5
  el-pagination(
    v-model:current-page="currentPage",
    layout="prev, pager, next",
    :page-count="props.pageCount",
    background,
    hide-on-single-page
  )
</template>

<script setup lang="ts">
import { type TwinResponse } from "@shared/services/twinManagement";
import { computed, onBeforeMount, ref, useSlots, watch } from "vue";
import { useMq } from "vue3-mq";
import TwinField from "./TwinField.vue";
import { formatDate } from "../../../util/formatting";
import dayjs from "dayjs";
import { useI18n } from "vue-i18n";
import UsersField from "./UsersField.vue";
import { type TwinProperty } from "../../../services/twinManagement";
import { useLocalCacheStore } from "@shared/stores/localStateCache";
import { useTwinStore } from "@shared/stores/twinManagement";
import { getRenderer } from "@shared/services/propertyRendererRegistry";
import { isNil } from "lodash";

const mq = useMq();

const { t } = useI18n();

function formatYear(date: string) {
  return dayjs(date).year();
}

function formatYesNo(booleanString: string) {
  return booleanString === "true" ? t("yes") : t("no");
}

const props = withDefaults(
  defineProps<{
    id?: string;
    loading?: boolean;
    assets: TwinResponse[];
    // NOTE: properties can be set by using the according template twin or using the property names directly,
    properties?: TwinProperty[];
    propertyIds?: string[];
    assetTwin?: TwinResponse;
    page: number;
    pageCount: number;
    hideDelete?: boolean;
    showCreated?: boolean;
    showId?: boolean;
    showDescription?: boolean;
    defaultSort?: { prop: string; order: "ascending" | "descending" };
  }>(),
  {
    id: undefined,
    properties: undefined,
    propertyIds: undefined,
    assetTwin: undefined,
    hideDelete: false,
    showCreated: false,
    showDescription: false,
    defaultSort: undefined,
  },
);

const emit = defineEmits(["rowClick", "sort", "page", "delete"]);

const localCacheStore = useLocalCacheStore();

onBeforeMount(() => {
  void twinStore.getTwinTemplates();
});

const assetProperties = computed(() => {
  // Either properties are defined manually, or through the template
  let ap = props.assetTwin
    ? props.assetTwin?.properties
        ?.filter((item) => item.isUserRelevant)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    : props.properties;
  // If propertyIds are set, they are used to determine visibility
  if (props.propertyIds) {
    ap =
      ap
        ?.filter((item) => props.propertyIds?.includes(item?.customId ?? ""))
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)) ?? [];
  }
  // On small screens we only show the first two properties
  return mq.mdPlus ? ap : ap?.slice(0, 1);
});

const assets = computed(() =>
  props.assets.map((twin) =>
    twin.properties?.reduce(
      (acc: TwinTableRow, curr) => {
        // acc[curr.name] = 'test';
        acc[curr.customId!] = curr.propertyValue?.value;
        return acc;
      },
      { id: twin.uuid, twin } as TwinTableRow,
    ),
  ),
);

type TwinTableRow = {
  id: string;
  twin: TwinResponse;
  [key: string]:
    | TwinResponse
    | NonNullable<TwinProperty["propertyValue"]>["value"]
    | undefined;
};

const sorting = ref<{
  order: string;
  sortBy: { customId?: string; type: string };
} | null>(null);

function sortChange(sort: { order: string | null; prop: string }) {
  if (sort.order === null) {
    sorting.value = null;
  } else {
    const sortOrder = sort.order === "ascending" ? "asc" : "desc";
    // The backend requires the type of the column, which we determine by using the templates property type
    const prop = assetProperties.value?.find((item) => item.name === sort.prop);
    if (prop) {
      const sortBy = { customId: prop.customId, type: prop.type };
      sorting.value = { order: sortOrder, sortBy: sortBy };
    } else {
      // This should never happen
      sorting.value = null;
    }
  }
  emit("sort", sorting.value);
}

function onRowClick(row: { twin: TwinResponse }) {
  emit("rowClick", row.twin);
}

function deleteTwin(row: { twin: TwinResponse }) {
  emit("delete", row.twin);
}

const currentPage = computed({
  get() {
    return props.page;
  },
  set(page: number) {
    emit("page", page);
  },
});

watch(currentPage, () => {
  // NOTE: only caceh paging if the table has an id
  if (props.id !== undefined) {
    localCacheStore.setPage(props.id, currentPage.value);
  }
});
onBeforeMount(() => {
  if (props.id !== undefined) {
    currentPage.value = localCacheStore.getPage(props.id) ?? 1;
  }
});

const slots = useSlots();
function hasSlot(name: string | undefined) {
  return name !== undefined && !!slots[name];
}

function formatCreatedAt(row: { twin: TwinResponse }) {
  return formatDate(row.twin.created);
}

const twinStore = useTwinStore();
function getTableRenderer(property: TwinProperty) {
  const templatePropUuid = property.propertyTemplateUuid ?? property.uuid;
  const twinType = twinStore.templateByPropertyUuid.get(templatePropUuid);
  return getRenderer(twinType, property.customId);
}
</script>

<style scoped>
:deep(.el-loading-mask) {
  z-index: 10 !important;
}
</style>
